/* eslint-disable @typescript-eslint/no-explicit-any */
import { FunctionComponent, useMemo, useRef } from "react";
import { Editor } from "@tinymce/tinymce-react";

import suggestionsDataForSignatures from "@/utils/suggestionsForSignatures";
import { dropDataUriImages } from "@/utils/dropDataUriImages";
import { SIGNATURE_VALID_ELEMENTS } from "@/utils/tinymceEditor.constants";

export const SignatureEditor: FunctionComponent<{
	signature: string;
	handleChange: (value: string) => void;
	handleInputChange?: (value: boolean) => void;
	maxLength?: number;
	placeholder?: string;
}> = ({ signature, handleChange, handleInputChange, maxLength, placeholder }) => {
	const editorRef = useRef<any>(null);
	const statusNodeRef = useRef<HTMLSpanElement | null>(null);

	const processedSuggestions = useMemo(() => {
		return Object.keys(suggestionsDataForSignatures.suggestions).map((key) => ({
			text: suggestionsDataForSignatures.suggestions[
				key as keyof typeof suggestionsDataForSignatures.suggestions
			] as string,
			value: key,
		}));
	}, []);

	return processedSuggestions ? (
		<Editor
			ref={editorRef}
			apiKey={import.meta.env.VITE_TINY_MCE} // optional for cloud features
			init={{
				placeholder: placeholder,
				menubar: false,
				height: 360,
				branding: false,
				elementpath: false,
				toolbar:
					"bold italic underline blocks forecolor link unlink fontfamily fontsize removeformat code",

				block_formats: "Paragraph=p;Heading 1=h1;Heading 2=h2;Heading 3=h3",
				content_style: `
					body { font-family: Arial, Helvetica, sans-serif; font-size:14px; }
					 
		  
          a { text-decoration: underline; }
          table, td { border-collapse: collapse; }
			
				  `,
				toolbar_mode: "wrap",
				plugins: "link lists paste code visualchars wordcount",
				font_family_formats: `
      Arial=arial,helvetica,sans-serif;
      Georgia=georgia,serif;
	  Helvetica=helvetica,sans-serif;
      Times New Roman='Times New Roman',times,serif;
      Tahoma=tahoma,verdana,sans-serif;
      Trebuchet MS='Trebuchet MS',helvetica,sans-serif;
      Verdana=verdana,geneva,sans-serif;
      Courier New='Courier New',courier,monospace
    `,
				// Keep the HTML simple & email-safe:
				forced_root_block: "p", // avoid <div>
				valid_children: "+body[p]",
				// Email-safe tags plus hosted images and the table/link attributes that
				// signature generators write (see constant).
				valid_elements: SIGNATURE_VALID_ELEMENTS,
				// URLs in email should be absolute:
				convert_urls: false,
				relative_urls: false,
				remove_script_host: false,
				paste_as_text: false,
				// A signature's inline styles are its layout (image sizes, colours,
				// display:block icons); WebKit paste strips them all by default.
				paste_webkit_styles: "all",
				paste_remove_styles_if_webkit: false,
				// Never inline pasted images as base64 (bloats campaignConfig → 413).
				paste_data_images: false,
				// Keep inline styles (fonts/colors) that email clients expect. Margins stay:
				// generators set `margin: 0.1px` to cancel the client's paragraph spacing.
				uploadcare_public_key: "fcdffd80684e1dd9782f",
				content_css: "/custom-tinymce.css",

				setup: function (editor: any) {
					// Hosted images stay; an inline base64 one is removed however it arrives.
					editor.on("PreInit", () => {
						editor.parser.addNodeFilter("img", dropDataUriImages);
					});

					editor.on("keyup", (e: KeyboardEvent) => {
						handleInputChange?.(true);
						const characterCount = editor.plugins.wordcount?.body?.getCharacterCount() ?? 0;
						const text = editor.getBody().innerText;

						if (
							maxLength &&
							characterCount >= maxLength &&
							!["Backspace", "Delete", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(
								e.key
							)
						) {
							e.preventDefault(); // stop further input

							editor.getBody().innerText = text.slice(
								0,
								text.length - (characterCount - maxLength)
							);
						}
					});

					editor.ui.registry.addAutocompleter("variables", {
						trigger: "{", // ← use this (not `ch`)
						minChars: 0, // show immediately after `{`
						// default predicate requires start-of-word; allow anywhere:
						matches: () => true,
						fetch: (pattern: any) => {
							const list = processedSuggestions.filter((v) =>
								v.text.toLowerCase().includes(pattern.toLowerCase())
							);

							return Promise.resolve(
								list.map((v) => ({ type: "autocompleteitem", text: v.text, value: v.value }))
							);
						},
						onAction: (api: any, rng: any, value: any) => {
							editor.selection.setRng(rng);

							// If the next char is already '}', avoid duplicating it.
							const node = editor.selection.getNode();
							const contentAfter =
								node?.textContent?.slice(editor.selection.getRng().startOffset) ?? "";
							const needsClose = !contentAfter?.startsWith("}");

							editor.insertContent(needsClose ? "{" + value + "}" : value);
							api.hide();
						},
					});

					const ensureStatusNode = () => {
						// Prefer the text container; fall back to full statusbar
						const container = editor.getContainer();
						const sb =
							container.querySelector(".tox-statusbar__text-container") ||
							container.querySelector(".tox-statusbar");

						if (sb && !statusNodeRef.current) {
							const el = document.createElement("span");

							el.className = "my-statusbar-left";
							el.textContent = "Type { to insert variables";
							// Put it at the start so it sits left of the element path/wordcount
							sb.prepend(el);
							statusNodeRef.current = el as HTMLSpanElement;
						}
					};

					// Run once the chrome is there; SkinLoaded covers theme load too
					editor.on("init SkinLoaded", ensureStatusNode);

					editor.on("remove", () => {
						statusNodeRef.current = null;
					});
				},
			}}
			initialValue={signature}
			onBlur={(e: any) => {
				let text = e.target.getContent();
				const editor = e.target.editorCommands?.editor;

				const characterCount = editor.plugins.wordcount?.body?.getCharacterCount() ?? 0;

				if (maxLength) {
					text = text.slice(0, text.length - (characterCount - maxLength));

					e.target.getContent = () => text.slice(0, text.length - (characterCount - maxLength));
				}

				handleChange(text);
			}}
			onInit={() => {
				(document.querySelector("button.tox-statusbar__wordcount") as HTMLElement)?.click();
			}}
		/>
	) : (
		<></>
	);
};
